import React, { type KeyboardEventHandler, useEffect } from 'react'
import Icon from '@/components/icon'
import ReactDOM from 'react-dom'
import {
  type ValueAnimationTransition,
  type PanInfo,
  animate,
  useIsomorphicLayoutEffect,
  useMotionValue,
  useMotionValueEvent,
} from 'framer-motion'
import { useEventListener } from 'usehooks-ts'

import usePrevious from '@/utils/usePrevious'
import useDebouncedCallback from '@/utils/useDebouncedCallback'

import VirtualizedSlide from './virtualized-slide-track'
import Close from '@/icons/bwi/close.svg'
import Download from '@/icons/bwi/download.svg'
import Desktop from '@/icons/bwi/desktop.svg'
import { getCircularIndex } from './utils'
import { cx } from '@/utils/strings'
import { isPrint } from '@/utils/print'
import { useTranslations } from '@/utils/locale'

export type SliderProps = {
  children: React.ReactNode
  customCss?: any
  sliderStyle?: 'Carousel' | 'Presentation'
  pdfPath?: string
}

const DEFAULT_RENDER_COUNT = 2
const isPrintView = isPrint()
function NavButton({ children, ...props }: React.ComponentProps<'button'>) {
  return (
    <button type="button" {...props}>
      {children}
    </button>
  )
}

function NavDot({
  ...props
}: {
  active: boolean
} & React.ComponentProps<'button'>) {
  return (
    <button
      type="button"
      role="tab"
      aria-current={props.active ? 'true' : undefined}
      data-testid="slider-nav-dot"
      className={props.className}
      onClick={props.onClick}
      aria-label={props['aria-label']}
    />
  )
}

const dragTransition: ValueAnimationTransition = {
  type: 'spring',
  bounce: 0,
}
const navigateTransition: ValueAnimationTransition = {
  type: 'spring',
  bounce: 0,
  stiffness: 5000,
  damping: 10,
}

/**
 * Infinite slider component
 */
export default function Slider({
  children,
  customCss,
  sliderStyle = 'Carousel',
  pdfPath = '',
  noBgNoPadding,
  hideLeftRightNav,
}: SliderProps) {
  const items = React.Children.toArray(children)
  const { t } = useTranslations()
  const x = useMotionValue(0)
  const parentRef = React.useRef<HTMLDivElement>(null)

  const containerRef = React.useRef<HTMLDivElement>(null)
  const [index, setIndex] = React.useState(0)
  const previousIndex = usePrevious(index)
  const activeIndex = getCircularIndex(index, items.length)

  const [renderCount, setRenderCount] = React.useState({
    forward: DEFAULT_RENDER_COUNT,
    behind: DEFAULT_RENDER_COUNT,
  })

  const [isFullscreen, setIsFullscreen] = React.useState(false)

  const calculateNewX = (i: number) => -i * (containerRef.current?.clientWidth || 0)

  const handleNavigateLeft = () => {
    animate(x, calculateNewX(index - 1), navigateTransition)
    setIndex(index - 1)
  }
  const handleNavigateRight = () => {
    animate(x, calculateNewX(index + 1), navigateTransition)
    setIndex(index + 1)
  }
  const handleKeyDown: KeyboardEventHandler = React.useCallback(
    (event) => {
      if (event.key === 'ArrowRight') {
        parentRef.current?.focus()
        handleNavigateRight()
      } else if (event.key === 'ArrowLeft') {
        parentRef.current?.focus()
        handleNavigateLeft()
      }
    },
    [index, setIndex]
  )

  const handleEndDrag = (e: Event, dragProps: PanInfo) => {
    if (!dragProps) return
    const clientWidth = containerRef.current?.clientWidth || 0

    const { offset, velocity } = dragProps

    if (Math.abs(velocity.y) > Math.abs(velocity.x)) {
      animate(x, calculateNewX(index), dragTransition)
      return
    }

    if (offset.x > clientWidth / 4) {
      setIndex(index - 1)
    } else if (offset.x < -clientWidth / 4) {
      setIndex(index + 1)
    } else {
      animate(x, calculateNewX(index), dragTransition)
    }
  }

  useEffect(() => {
    const delta = index - (previousIndex || 0)
    if (delta > DEFAULT_RENDER_COUNT) {
      setRenderCount({ forward: delta, behind: DEFAULT_RENDER_COUNT })
    } else if (delta < -DEFAULT_RENDER_COUNT) {
      setRenderCount({ forward: DEFAULT_RENDER_COUNT, behind: Math.abs(delta) })
    }
    const controls = animate(x, calculateNewX(index), dragTransition)

    return () => {
      controls.stop
    }
  }, [index])

  const handleResize = useDebouncedCallback(() => {
    // Render correct x position after resize
    x.set(calculateNewX(index))
  }, 50)

  useIsomorphicLayoutEffect(() => {
    handleResize()
  }, [])
  useEventListener('resize', handleResize)

  useMotionValueEvent(x, 'animationStart', () => {
    if (parentRef.current) {
      parentRef.current.ariaBusy = 'true'
    }
  })

  useMotionValueEvent(x, 'animationComplete', () => {
    if (parentRef.current) {
      parentRef.current.ariaBusy = 'false'
    }

    // Reset render count to default after animation is complete
    if (renderCount.forward !== DEFAULT_RENDER_COUNT || renderCount.behind !== DEFAULT_RENDER_COUNT) {
      setRenderCount({
        forward: DEFAULT_RENDER_COUNT,
        behind: DEFAULT_RENDER_COUNT,
      })
    }
  })

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen)
    handleResize()
  }

  const NavBtnStyles =
    'border flex items-center justify-center w-9 h-9 text-[16px] rounded-full bg-white dark:bg-secondary-900 shadow-md hover:bg-secondary-50 hover:bg-secondary-50 dark:hover:bg-secondary-800 active:scale-95 will-change-transform transition-[opacity, transform] duration-100 select-none'

  const CarouselNavBtnStyles = 'absolute top-0 bottom-0 my-auto z-20 '
  const renderSliderNavButtons = () => (
    <>
      <NavButton
        aria-label={t('Navigate left')}
        className={cx(
          sliderStyle !== 'Presentation' &&
            `left-(--bw-slider-nav-button-distance) ${NavBtnStyles} ${CarouselNavBtnStyles}`,
          sliderStyle === 'Presentation' && `border ${NavBtnStyles}`
        )}
        onClick={handleNavigateLeft}
        data-testid="slider-nav-left"
      >
        <span className="bwi bwi-angle-left text-primaryBlue w-[16px] h-[16px] leading-none -translate-x-[0.0625rem]" />
      </NavButton>
      {sliderStyle === 'Presentation' && (
        // display the slide number
        <div
          className={cx(
            !isFullscreen && 'text-black dark:text-white',
            isFullscreen && 'text-white',
            'text-sm w-14 text-center'
          )}
        >
          {activeIndex + 1} / {items.length}
        </div>
      )}
      <NavButton
        aria-label={t('Navigate right')}
        className={cx(
          sliderStyle !== 'Presentation' &&
            `right-(--bw-slider-nav-button-distance) ${NavBtnStyles} ${CarouselNavBtnStyles}`,
          sliderStyle === 'Presentation' && NavBtnStyles
        )}
        onClick={handleNavigateRight}
        data-testid="slider-nav-right"
      >
        <span className="bwi bwi-angle-right text-primaryBlue w-[16px] h-[16px] leading-none translate-x-0.5" />
      </NavButton>
    </>
  )

  const SliderWrapper = React.useCallback(
    ({ children }: { children: React.ReactNode }) => {
      if (isFullscreen) {
        const appElement = document.getElementById('app')
        if (appElement) {
          return ReactDOM.createPortal(
            <div className="fixed inset-0 bg-black flex items-center justify-center z-50">
              <div className="w-full h-full flex flex-col justify-center">
                <div className="p-10">{children}</div>
                <button
                  onClick={toggleFullscreen}
                  className="absolute top-4 right-4 bg-black bg-opacity-70 text-white p-2 rounded-full hover:bg-opacity-90 transition-all z-10"
                  aria-label={t('Close fullscreen')}
                >
                  <Close className="text-white text-4xl w-6" />
                </button>
              </div>
            </div>,
            appElement
          )
        }
      }
      return <>{children}</>
    },
    [isFullscreen]
  )

  return !isPrintView ? (
    <SliderWrapper>
      <div
        role="tablist"
        className={cx(
          'outline-hidden w-full overflow-hidden [--bw-slider-nav-button-distance:0.5rem]',
          sliderStyle === 'Presentation' && 'border shadow-even-around-small',
          !noBgNoPadding && 'rounded-4xl',
          customCss
        )}
        ref={parentRef}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        data-testid="slider"
      >
        <div className="relative w-full">
          {sliderStyle !== 'Presentation' && !hideLeftRightNav && renderSliderNavButtons()}
          <VirtualizedSlide
            x={x}
            index={index}
            totalCount={items.length}
            onDragEnd={handleEndDrag}
            containerRef={containerRef}
            forwardRenderCount={renderCount.forward}
            behindRenderCount={renderCount.behind}
          >
            {({ index: currentIndex }) => {
              const itemIndex = getCircularIndex(currentIndex, items.length)
              return <>{items[itemIndex]}</>
            }}
          </VirtualizedSlide>

          {sliderStyle !== 'Presentation' && (
            <div
              role="tablist"
              className={cx(
                'slider-dots flex absolute bottom-[24px] w-full  z-20',
                !noBgNoPadding && 'items-center justify-center'
              )}
            >
              {items.map((item, itemIndex) => (
                <NavDot
                  key={itemIndex}
                  active={itemIndex === activeIndex}
                  aria-label={`Go to slide ${itemIndex + 1} of ${items.length}`}
                  className={cx(
                    'w-2 h-2 rounded-full hover:opacity-80 transition-[opacity, transform] duration-200 mx-1 bg-primaryBlue',
                    itemIndex === activeIndex && `opacity-100 scale-125`,
                    itemIndex !== activeIndex && `opacity-50 scale-100`,
                    itemIndex !== activeIndex && `hover:opacity-100`
                  )}
                  onClick={() => {
                    setIndex(itemIndex)
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </div>
      {sliderStyle === 'Presentation' && (
        <div className="flex flex-row py-4 space-x-3 items-center px-6">
          <div className="flex-grow">
            {items.length > 1 && <div className=" flex flex-row items-center ">{renderSliderNavButtons()}</div>}
          </div>

          <div className=" flex flex-row items-center gap-3">
            {!isFullscreen && (
              <button
                className={`border ${NavBtnStyles}`}
                onClick={toggleFullscreen}
                title={t('Go Fullscreen')}
                type="button"
              >
                <Desktop className="text-primaryBlue w-5 h-5" />
              </button>
            )}
            {pdfPath && (
              <a href={pdfPath} target="_blank">
                <button className={`border ${NavBtnStyles}`} title={t('Download PDF')} type="button">
                  <Download className="text-primaryBlue w-5 h-5" />
                </button>
              </a>
            )}
          </div>
        </div>
      )}
    </SliderWrapper>
  ) : (
    <div className="[&_section]:mb-10">{children}</div>
  )
}
